import Vue from 'vue';
import type { ActionTree, ActionContext, Module, MutationTree } from 'vuex';
import { DateTime } from 'luxon';
import {
	ExperienceTypeConstant,
	MemberStateConstant,
	RecentClubModuleConstant,
	StorageConstant
} from '../../constants';
import { recentClubService } from '../../services/recentClubService';
import { ltLuxon } from '../../helpers/ltLuxon';
import { profileService, storageService } from '@life-time-inc/enterprise-framework-core';
import type { ltFramework } from '@life-time-inc/enterprise-framework-core';

interface IRecentClubState {
	store: ltFramework.IRecentClubStoreData;
}

const RecentClubState: IRecentClubState = {
	store: {
		clubs: [],
		initialized: false
	}
};

const { action, mutation } = RecentClubModuleConstant;

const RecentClubMutations: MutationTree<IRecentClubState> = {
	[mutation.setRecentClubs](state: IRecentClubState, payload: ltFramework.IRecentClubStoreData) {
		state.store = payload;
	},

	[mutation.addRecentClub](state: IRecentClubState, payload: ltFramework.IRecentClub[]) {
		Vue.set(state.store, 'clubs', payload);
	}
};

const RecentClubActions: ActionTree<IRecentClubState, ltFramework.IRootState> = {
	[action.initialize](
		{ commit }: ActionContext<IRecentClubState, ltFramework.IRootState>,
		initObj: ltFramework.IRecentClubStoreInitObj
	): Promise<void> {
		return new Promise<void>((resolve) => {
			const recentClubData: ltFramework.IRecentClubStoreData = {
				clubs: [],
				initialized: false
			};
			let lastClub: ltFramework.IRecentClub = {} as ltFramework.IRecentClub;
			let profileClubs: ltFramework.IRecentClub[] = [];
			let updateProfile = false;

			// get stored data
			const recentClubsStorage: ltFramework.IRecentClub[] = storageService.localGet<
				ltFramework.IRecentClub[]
			>(StorageConstant.recentClubs) as ltFramework.IRecentClub[];

			let storedData: ltFramework.IRecentClub[] = [];

			if (recentClubsStorage) {
				storedData = recentClubsStorage.filter((club: ltFramework.IRecentClub) => {
					return /^(\/content\/lt\/en_us\/clubs|\/clubs)(\/[^/]+){1,2}\.html$/.exec(club.link);
				});

				if (storedData.length) {
					lastClub = storedData[0];
				}
			}

			if (initObj.profile.personalization?.recentClubs) {
				profileClubs = initObj.profile.personalization.recentClubs;
			}

			// check if we need to update profile info / merge data
			if (profileClubs.length) {
				// set update flag
				if (
					initObj.experienceType === ExperienceTypeConstant.member
					&& initObj.profile.memberState !== MemberStateConstant.unknown
					&& initObj.profile.memberState !== MemberStateConstant.unauthenticated
				) {
					const profileLastClub: ltFramework.IRecentClub = profileClubs[0];
					// If we don't have a last club coming from profile
					if (!profileLastClub) {
						updateProfile = true;
					}
					// if the most recent club in the profile doesnt match what we have in storage and it's been more than 5 minutes since the last update to the profile, we set the update flag to true
					else if (
						lastClub
						&& profileLastClub.id !== lastClub.id
						&& profileLastClub.date
						&& lastClub.date
						&& ltLuxon(profileLastClub.date) < ltLuxon(lastClub.date).minus({ minutes: 5 })
					) {
						updateProfile = true;
					}
					// Merge Data before submitting the profile update
					recentClubData.clubs = recentClubService.mergeData(profileClubs, storedData);
					if (updateProfile) {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						profileService.updateRecentClubs(recentClubData.clubs);
					}
				}
			}
			else {
				recentClubData.clubs = storedData;
			}

			// set cookie
			storageService.localSet(StorageConstant.recentClubs, recentClubData.clubs);

			recentClubData.initialized = true;

			commit(mutation.setRecentClubs, recentClubData);

			resolve();
		});
	},

	[action.addRecentClub](
		{ commit }: ActionContext<IRecentClubState, ltFramework.IRootState>,
		obj: ltFramework.IAddRecentClubObj
	): Promise<void> {
		return new Promise<void>((resolve) => {
			const recentClubsStorage: ltFramework.IRecentClub[] = storageService.localGet<
				ltFramework.IRecentClub[]
			>(StorageConstant.recentClubs) as ltFramework.IRecentClub[];

			if (obj.club.id && obj.club.name && obj.club.link) {
				const recentClubObj: ltFramework.IRecentClub = {
					id: obj.club.id.toString(),
					name: obj.club.name,
					date: DateTime.now().toISO(),
					link: obj.club.link
				};

				const newRecentClubs: ltFramework.IRecentClub[] = recentClubService.addClub(
					recentClubObj,
					recentClubsStorage
				);

				storageService.localSet(StorageConstant.recentClubs, newRecentClubs);

				commit(mutation.addRecentClub, newRecentClubs);
			}

			// if we're on a global page then reload the page so the new club can be reflected
			if (obj.reload) {
				window.location.reload();
			}

			resolve();
		});
	}
};

export const RecentClubModule: Module<IRecentClubState, ltFramework.IRootState> = {
	namespaced: true,
	state: RecentClubState,
	actions: RecentClubActions,
	mutations: RecentClubMutations
};
